/* eslint-disable css-modules/no-unused-class  */
import { useState, FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { translate } from 'translations';
import { selectIsLeanUser } from '@redux/slices/userSlice';
import { Icon, AccordionTable, CollapseTableData, Avatar } from 'learning-panel-components';
import classNames from 'classnames';
import styles from './userLeaderboard.module.scss';

const getClassName = (baseClass: string, index: number) =>
    classNames(styles[baseClass], { [styles['first-row']]: index === 0 });

export const getRow = (
    row: UserData,
    index: number,
    currentUserNickname: string,
    youText: string,
    showTeamsNames?: boolean,
    addTeamElement?: boolean
) => {
    const rowRank = Number(row.rank);
    const isTopThree = rowRank === 1 || rowRank === 2 || rowRank === 3;
    const iconElement = (
        <div key={`${index}-icon`} className={styles['rank-wrapper']}>
            {isTopThree && (
                <img
                    alt="rankIcon"
                    className={styles['top-rank']}
                    src={`${process.env.PUBLIC_URL}/images/topRank.svg`}
                />
            )}
            <div className={getClassName('row-rank', index)}>{`#${row.rank}`}</div>
        </div>
    );
    const nameElement = (
        <div key={`${index}-name`} className={getClassName('row-name', index)}>
            {addTeamElement ? (
                <Avatar name={row.nickname} avatarSrc={row.profilePicture} />
            ) : (
                <img
                    className={styles.avatar}
                    src={`${process.env.PUBLIC_URL}/images/avatars/tournaments/${row.avatar}.svg`}
                    alt="avatar"
                />
            )}
            <div title={row.nickname} className={styles.text}>
                {row.nickname === currentUserNickname ? youText : row.nickname}
            </div>
        </div>
    );

    let teamNamesElement = null;
    if (addTeamElement) {
        teamNamesElement = (
            <div key={`${index}-team`} title={row.teams} className={getClassName('row-teams', index)}>
                {row.teams}
            </div>
        );
    }

    const scoreElement = (
        <div key={`${index}-score`} className={styles['row-score']}>
            <Icon name="devCoin" className={styles['dev-coin']} />
            <div>{row.score.toLocaleString()}</div>
        </div>
    );
    const rowDivs = [iconElement, nameElement, scoreElement];
    teamNamesElement && showTeamsNames && rowDivs.splice(2, 0, teamNamesElement);
    return rowDivs;
};

const UserLeaderboard: FC<UserLeaderboardProps> = (props: UserLeaderboardProps) => {
    const {
        currentUser,
        users,
        onLoadMore,
        hasMore,
        tableHeight = '44rem',
        currentUserTop = '5rem',
        showTeamsNames,
        isTournament
    } = props;
    const youText = translate('You');

    const [rows, setRows] = useState<CollapseTableData[]>([]);
    const isLeanUser = useSelector(selectIsLeanUser);

    useEffect(() => {
        const tmpRows: CollapseTableData[] = users?.map((user: UserData, i: number) => ({
            row: getRow(user, i, currentUser.nickname, youText, showTeamsNames, !isTournament)
        }));
        setRows(tmpRows);
    }, [users]);

    return (
        <>
            <div style={{ '--currentUserTop': currentUserTop } as any} className={styles['current-user']}>
                {getRow(currentUser, -1, currentUser.nickname, youText, showTeamsNames, !isTournament)}
            </div>
            {rows.length > 0 && (
                <AccordionTable
                    rows={rows}
                    title=""
                    isOpen={true}
                    hasMoreRows={hasMore}
                    loadMoreRows={onLoadMore}
                    showTitle={false}
                    tableHeight={isLeanUser ? '23rem' : tableHeight}
                />
            )}
        </>
    );
};

interface UserLeaderboardProps {
    currentUser: UserData;
    users: UserData[];
    onLoadMore: Function;
    hasMore: boolean;
    tableHeight?: string;
    currentUserTop?: string;
    showTeamsNames?: boolean;
    isTournament: boolean;
}

export interface UserData {
    rank: number;
    nickname: string;
    score: number;
    profilePicture?: string;
    avatar?: string;
    teams?: string;
}

export default UserLeaderboard;
